import { useEffect, useId, useRef, useState } from 'react';
import type { SiteBriefSurveyType } from '../api.types';
import { resolveChrome } from '../utils/siteBriefChoiceChrome';
import {
  describeSiteBriefChoiceAnswer,
  formatSiteBriefSurveyAnswers,
  getSiteBriefSurveyCopy,
  hasSiteBriefChoiceAnswer,
  restoreSiteBriefSurveyDraft,
  SITE_BRIEF_SURVEY_ANSWER_MAX_LENGTH,
  skipSiteBriefSurveyQuestion,
} from '../utils/siteBriefSurvey';
import type { SiteBriefChoiceDraft, SiteBriefSurveyDraft } from '../utils/siteBriefSurvey';
import SiteBriefChoiceCard from './SiteBriefChoiceCard';
import SiteBriefQuestionPanel, { QuestionIcon } from './SiteBriefQuestionPanel';

const EMPTY_ANSWER: SiteBriefChoiceDraft = { selected: [], customText: '', customSelected: false };

function saveDraft(storageKey: string, draft: SiteBriefSurveyDraft) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // Keep the current in-memory answers usable when browser storage is unavailable.
  }
}

export default function SiteBriefSurvey({
  survey,
  storageKey,
  disabled,
  onSubmit,
}: {
  survey: SiteBriefSurveyType;
  storageKey: string;
  disabled: boolean;
  onSubmit: (answer: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState(() => {
    try {
      return restoreSiteBriefSurveyDraft(survey, sessionStorage.getItem(storageKey));
    } catch {
      return restoreSiteBriefSurveyDraft(survey, null);
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const submitLock = useRef(false);
  const content = useRef<HTMLDivElement>(null);
  const navigation = useRef<HTMLDivElement>(null);
  const counter = useRef<HTMLButtonElement>(null);
  const previousStep = useRef(draft.step);
  const listId = useId();
  const textId = useId();
  const copy = getSiteBriefSurveyCopy(survey.language);
  const chrome = resolveChrome(survey.language);
  const total = survey.questions.length;
  // A group of one question has no navigation.
  const grouped = total > 1;
  const question = survey.questions[draft.step];
  const answer = draft.answers[question.id] ?? EMPTY_ANSWER;
  const last = draft.step === total - 1;
  const locked = disabled || submitting;
  const skipped = draft.skipped.includes(question.id);
  // Next and Send wait for an answer or an explicit skip; the arrows move freely.
  const resolved = skipped || hasSiteBriefChoiceAnswer(question, answer);
  const answeredCount = survey.questions.filter((item) => hasSiteBriefChoiceAnswer(item, draft.answers[item.id])).length;

  useEffect(() => {
    saveDraft(storageKey, draft);
  }, [draft, storageKey]);

  useEffect(() => {
    if (previousStep.current !== draft.step) {
      content.current?.focus();
      previousStep.current = draft.step;
    }
  }, [draft.step]);

  useEffect(() => {
    if (!listOpen) {
      return;
    }
    const closeOutside = (event: MouseEvent) => {
      if (!navigation.current?.contains(event.target as Node)) {
        setListOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setListOpen(false);
        counter.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [listOpen]);

  const goTo = (step: number) => {
    setDraft((current) => ({ ...current, step }));
  };

  const updateAnswer = (value: SiteBriefChoiceDraft) => {
    setDraft((current) => ({
      ...current,
      answers: { ...current.answers, [question.id]: value },
      skipped: hasSiteBriefChoiceAnswer(question, value)
        ? current.skipped.filter((id) => id !== question.id)
        : current.skipped,
    }));
  };

  const submit = async (submission = draft) => {
    if (locked || submitLock.current) {
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    // Persist a skip of a single question before admission can unmount the widget.
    saveDraft(storageKey, submission);
    try {
      // Questions left unanswered go out with their skip sentence.
      if (await onSubmit(formatSiteBriefSurveyAnswers(survey, submission))) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          // Admission succeeded even if browser storage could not be cleared.
        }
      }
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const advance = () => {
    if (locked || !resolved) {
      return;
    }
    if (last) {
      void submit();
    } else {
      goTo(draft.step + 1);
    }
  };

  // In a group a skip clears the answer and moves on, staying on the last question until the answers are sent;
  // a single question sends its skip at once, like a single card.
  const skip = () => {
    if (locked || submitLock.current) {
      return;
    }
    const next = skipSiteBriefSurveyQuestion(survey, draft);
    setDraft(next);
    if (!grouped) {
      void submit(next);
    }
  };

  const summary = grouped ? (
    <>
      <span className="sb-question-panel__muted">{copy.answered(answeredCount, total)} · </span>
      {copy.upNext} <strong>{question.question}</strong>
    </>
  ) : (
    <strong>{question.question}</strong>
  );

  return (
    <SiteBriefQuestionPanel chrome={chrome} summary={summary} lang={survey.language}>
      <div className="sb-survey__question" ref={content} tabIndex={-1}>
        {question.kind === 'free_text' ? (
          <div className="sb-choice-card" key={question.id}>
            <div className="sb-choice-card__header">
              <h3 className="sb-choice-card__question">
                <label htmlFor={textId}>{question.question}</label>
              </h3>
              {skipped && (
                <p className="sb-choice-card__hint">
                  <span className="sb-choice-card__chip">{chrome.skipped}</span>
                </p>
              )}
            </div>
            <div className="sb-choice-card__body">
              <textarea
                id={textId}
                className="sb-survey__text"
                value={answer.customText}
                disabled={locked}
                maxLength={SITE_BRIEF_SURVEY_ANSWER_MAX_LENGTH}
                rows={3}
                placeholder={question.placeholder ?? copy.placeholder}
                onChange={(event) => updateAnswer({ selected: [], customText: event.target.value, customSelected: true })}
              />
            </div>
          </div>
        ) : (
          <SiteBriefChoiceCard
            key={question.id}
            choice={{ ...question, kind: question.kind }}
            draft={answer}
            onDraftChange={updateAnswer}
            disabled={locked}
            embedded
            skipped={skipped}
            onEnter={advance}
            fallbackLanguage={survey.language}
            customAnswerMaxLength={SITE_BRIEF_SURVEY_ANSWER_MAX_LENGTH}
          />
        )}
      </div>
      {grouped && (
        <span className="sb-sr-only" role="status">
          {copy.question} {copy.counter(draft.step + 1, total)}
        </span>
      )}
      <div className="sb-survey__footer">
        {grouped && (
          <div className="sb-survey__nav" ref={navigation}>
            <button
              type="button"
              className="sb-survey__arrow"
              aria-label={copy.previousQuestion}
              disabled={locked || draft.step === 0}
              onClick={() => goTo(draft.step - 1)}
            >
              <QuestionIcon name="chevron-left" />
            </button>
            <button
              ref={counter}
              type="button"
              className="sb-survey__counter"
              aria-expanded={listOpen}
              aria-controls={listOpen ? listId : undefined}
              aria-label={copy.showAll(draft.step + 1, total)}
              disabled={locked}
              onClick={() => setListOpen((current) => !current)}
            >
              {copy.counter(draft.step + 1, total)}
            </button>
            {listOpen && (
              <div className="sb-survey__list" id={listId} role="region" aria-label={copy.allQuestions}>
                <div className="sb-survey__list-head">
                  <span>{copy.answered(answeredCount, total)}</span>
                  {draft.skipped.length > 0 && <span>{copy.skippedCount(draft.skipped.length)}</span>}
                </div>
                <ol>
                  {survey.questions.map((item, index) => {
                    const current = index === draft.step;
                    const itemAnswer = draft.answers[item.id];
                    const done = hasSiteBriefChoiceAnswer(item, itemAnswer);
                    const status = current
                      ? copy.current
                      : done
                        ? describeSiteBriefChoiceAnswer(item, itemAnswer)
                        : draft.skipped.includes(item.id)
                          ? chrome.skipped
                          : '';
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={`sb-survey__list-item ${current ? 'is-current' : ''} ${done ? 'is-done' : ''}`}
                          aria-current={current ? 'step' : undefined}
                          onClick={() => {
                            goTo(index);
                            setListOpen(false);
                          }}
                        >
                          <span className="sb-survey__list-number" aria-hidden="true">
                            {index + 1}
                          </span>
                          <span className="sb-survey__list-copy">
                            <span className="sb-survey__list-question">
                              <span className="sb-sr-only">
                                {copy.question} {index + 1}:{' '}
                              </span>
                              {item.question}
                            </span>
                            {status && <span className="sb-survey__list-status">{status}</span>}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            <button
              type="button"
              className="sb-survey__arrow"
              aria-label={copy.nextQuestion}
              disabled={locked || last}
              onClick={() => goTo(draft.step + 1)}
            >
              <QuestionIcon name="chevron-right" />
            </button>
          </div>
        )}
        <div className="sb-survey__actions">
          <button
            type="button"
            className="sb-question-button sb-question-button--ghost"
            disabled={locked}
            onClick={skip}
          >
            {copy.skip}
          </button>
          <button
            type="button"
            className="sb-question-button sb-question-button--primary"
            disabled={locked || !resolved}
            onClick={advance}
          >
            {submitting && <span className="sb-spinner" />}
            {!last ? copy.next : grouped ? copy.submit : chrome.send}
            <QuestionIcon name="arrow-right" />
          </button>
        </div>
      </div>
    </SiteBriefQuestionPanel>
  );
}
